import { differenceInDays } from "date-fns";
import { saveAs } from "file-saver";

import { PdfReport } from "../../../models/pdf.report";
import { Translate } from "../UploadCSVFile/types";
import { ColumnMappingCSV } from "../../../util/readCSV";

export interface SearchAttribute {
  attribute: string;
  values: string[];
}

export enum SearchColumOption {
  ORDER = "products.poNumber",
  SSCC = "sscc",
  SKU = "products.sku",
  DESCRIPTION = "products.description",
  COLOR = "products.colorDescription",
}

export interface SubTitleProps {
  text?: string;
  quantity?: number;
}

export const MIN_DAY_DIFFERENCE_SHIP_DATA = 5;
export const MAX_DAY_DIFFERENCE_SHIP_DATA = 21;
export const MAX_DAY_DIFFERENCE_CARGO_READY_DATE = 14;

export function generateRandomSSCC(length: number) {
  let result = "";
  const charactersInUse = "0123456789";
  const charactersLength = charactersInUse.length;

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < length; i++) {
    result += charactersInUse.charAt(Math.floor(Math.random() * charactersLength));
  }

  return result;
}

export const getLowerLimitPercentage = (limit: number) => {
  return (100 - limit) / 100;
};

export const getUpperLimitPercentage = (limit: number) => {
  return (100 + limit) / 100;
};

export const validatequantityInCarton = (
  filledQuantity: number | undefined,
  orderQuantity: number | undefined,
  upperLimit: number,
  lowerLimit: number
) => {
  return (
    filledQuantity &&
    orderQuantity &&
    filledQuantity <= orderQuantity * upperLimit &&
    filledQuantity >= orderQuantity * lowerLimit
  );
};

export const validateEarlyOrderBooking = (shipByDate?: string) => {
  if (!shipByDate) return false;

  const today = new Date();
  const difference = differenceInDays(new Date(shipByDate), today);

  if (difference > MAX_DAY_DIFFERENCE_SHIP_DATA) return false;

  return true;
};

export const validateLateOrderBooking = (shipByDate?: string) => {
  if (!shipByDate) return false;

  const today = new Date();
  const difference = differenceInDays(new Date(shipByDate), today);

  if (difference < MIN_DAY_DIFFERENCE_SHIP_DATA) return false;

  return true;
};

export const validateLateCargoReadyDate = (shipByDate?: string, cargoReadyDate?: string) => {
  if (!shipByDate || !cargoReadyDate) return false;

  const difference = differenceInDays(new Date(shipByDate), new Date(cargoReadyDate));

  if (difference < MAX_DAY_DIFFERENCE_CARGO_READY_DATE && difference >= 0) return true;

  return false;
};

export enum CartonSSCCFrom {
  BOOKING = "booking",
  PURCHASE_ORDER = "purchase_order",
  SHIPMENT = "shipment",
}

export interface CartonTemplateCSV {
  poNumber: string;
  cartonReference: string;
  sku: string;
  quantity: string;
  cbm: string;
  weight: string;
  originCriterion?: string;
  hsCode: string;

  construction: string;
  lining: string;
  category: string;
  fabric: string;
  composition: string;
  manufactureDetails: string;
  length?: string;
  gender?: string;
  container?: string;
  unitOfMeasure?: string;
}

export const getCartonTemplateColumnName = (translate: Translate) => {
  return {
    poNumber: "PurchaseOrder Number",
    cartonReference: "Carton Name",
    sku: "SKU",
    quantity: "Quantity",
    cbm: "CBM",
    weight: "Weight",
    originCriterion: "Dest Uk Criterion (W or P)",
    hsCode: "HSCODE",
    construction: "Construction",
    lining: "Lining",
    category: "Category",
    fabric: "Fabric",
    composition: "Composition",
    manufactureDetails: "Manufacturer Details",
    gender: "Gender",
    length: "Length",
    unitOfMeasure: "Unit of Measure",
  };
};

/**
 * Builds a ColumnMappingCSV that maps CSV header names (from the template)
 * to CartonTemplateCSV property names. This is the inverse of getCartonTemplateColumnName.
 *
 * Use this with `readCSV` so that column order in the CSV no longer matters.
 */
export const getCartonCSVHeaderMapping = (
  translate: Translate
): ColumnMappingCSV<CartonTemplateCSV> => {
  const columnNames = getCartonTemplateColumnName(translate);

  return Object.fromEntries(
    Object.entries(columnNames).map(([key, header]) => [header, key])
  ) as ColumnMappingCSV<CartonTemplateCSV>;
};

export interface CartonReceiveTemplateCSV {
  sscc: string;
}

export const getCartonReceiveTemplateColumnName = (translate: Translate) => {
  return {
    sscc: translate("sscc"),
  };
};

export const saveLabelReport = (data: PdfReport) => {
  if (!data.content) return;

  const blob = new Blob([new Uint8Array(data.content).buffer], {
    type: "application/pdf",
  });

  let filename = data?.name;
  if (data?.type && !data?.name?.endsWith(data?.type)) {
    filename = `${data?.name}.${data?.type}`;
  }

  saveAs(blob, `${filename}`);
};
